"use client";

import React from "react";
import { Activity, ShieldCheck, Leaf, Cpu, Layers, FlaskConical, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ApplicationItem {
  id: string;
  title: string;
  category: string;
  description: string;
  keyFeature: string;
  link: string;
  iconName: string;
}
const applications: ApplicationItem[] = [{
  id: "app-1",
  title: "Process Control & PAT",
  category: "Real-Time Analytics",
  description: "In-line and at-line analytical probes delivering continuous reaction monitoring, blend homogeneity verification, and precise endpoint determination.",
  keyFeature: "Reduces cycle times and eliminates batch-to-batch variability",
  link: "/ProcessAnalyticalTechnologyPage",
  iconName: "Cpu"
}, {
  id: "app-2",
  title: "Quality Assurance & QA/QC",
  category: "Analytical Testing",
  description: "High-precision instrumentation for raw material ID, tablet physical characterization, and comprehensive final batch release testing in cGMP settings.",
  keyFeature: "Designed to support compliance in regulated environments",
  link: "/TabletTestingPage",
  iconName: "ShieldCheck"
}, {
  id: "app-4",
  title: "Safety & Hazard Compliance",
  category: "Plant Safety",
  description: "Rapid non-destructive screening of unknown chemicals, hazardous materials, and volatile organics in warehouse and production transfer zones.",
  keyFeature: "Instant spectroscopic verification through sealed containers",
  link: "/SafetyAndSecurityPage",
  iconName: "Activity"
}, {
  id: "app-4",
  title: "Environmental & Solvent Recovery",
  category: "Sustainability",
  description: "Closed-loop HPLC solvent recycling systems and continuous emission surveillance to lower operating waste and meet stringent environmental targets.",
  keyFeature: "Recovers up to 95% of pure mobile phase solvents",
  link: "/HplcSolventRecoveryResolvePage",
  iconName: "Leaf"
}, {
  id: "app-5",
  title: "Powder & Granule Characterization",
  category: "Formulation Control",
  description: "Comprehensive physical testing solutions measuring powder flowability, tap density, shear stress, and compressibility for solid dose manufacturing.",
  keyFeature: "Predicts hopper feed performance and tablet press filling",
  link: "/PowderTestingPage",
  iconName: "Layers"
}, {
  id: "app-6",
  title: "Drying Endpoint & Moisture Analysis",
  category: "Process Optimization",
  description: "Non-invasive NIR and microwave sensing for dynamic moisture monitoring during fluid bed drying, vacuum tray drying, and lyophilization cycles.",
  keyFeature: "Eliminates over-drying and thermal degradation risks",
  link: "/DryingEndpointPage",
  iconName: "FlaskConical"
}];
export default function ManufacturingApplications() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Cpu":
        return <Cpu className="size-6 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-6 text-primary" />;
      case "Activity":
        return <Activity className="size-6 text-primary" />;
      case "Leaf":
        return <Leaf className="size-6 text-primary" />;
      case "Layers":
        return <Layers className="size-6 text-primary" />;
      case "FlaskConical":
      default:
        return <FlaskConical className="size-6 text-primary" />;
    }
  };
  return <section id="manufacturingapplications" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3">
            Manufacturing Solutions
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Chemical & Regulated Manufacturing Applications
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            Tailored analytical systems, qualification methodologies, and lifecycle instrumentation 
            designed for chemical processors, active ingredient synthesis, and regulated production facilities.
          </p>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {applications.map((app, index) => <motion.div key={app.id} data-index={index} initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : index * 0.08
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2.5 rounded-lg bg-secondary inline-flex items-center justify-center">
                      {getIcon(app.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-normal">
                      {app.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground font-default">
                    {app.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm font-sans leading-relaxed">
                    {app.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="flex items-start gap-2 p-3 rounded-md bg-secondary/50 border border-border/50 text-xs text-foreground font-sans">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{app.keyFeature}</span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between">
                    <Link to={app.link}>
                      <span>Explore application</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA Strip */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-xl bg-secondary border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1 max-w-xl">
            <h3 className="text-lg font-semibold text-secondary-foreground font-default">
              Need a custom analytical integration for your process line?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Speak directly with our application specialists for technical feasibility reviews and validation support.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/ContactPage">
              Request Technical Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}